import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Check, Plus, X } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FUNCTIONS = [
  { id: "procurement", name: "Procurement" },
  { id: "finance",     name: "Finance" },
  { id: "marketing",   name: "Marketing" },
  { id: "hr",          name: "HR" },
  { id: "sales",       name: "Sales" },
  { id: "operations",  name: "Operations" },
  { id: "general",     name: "General" },
];

const TOOLS = ["ChatGPT", "Claude", "Gemini", "Copilot", "Perplexity"];

const ContributePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", function_id: "", category: "",
    business_context: "", when_to_use: "", input_required: "",
    main_prompt: "", expected_output: "",
    pro_tips: [], recommended_tools: [],
    consent_flag: false, terms_version: "1.0"
  });
  const [tipInput, setTipInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center max-w-sm">
          <h2 className="font-bold text-slate-900 text-lg mb-3">Sign in Required</h2>
          <p className="text-slate-600 text-sm mb-5">You need to be signed in to contribute an application.</p>
          <Button onClick={() => navigate("/auth")} className="bg-blue-600 hover:bg-blue-700 text-white">Sign In</Button>
        </div>
      </div>
    );
  }

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target?.value ?? e }));

  const addTip = () => {
    if (tipInput.trim()) {
      setForm(f => ({ ...f, pro_tips: [...f.pro_tips, tipInput.trim()] }));
      setTipInput("");
    }
  };

  const removeTip = (i) => setForm(f => ({ ...f, pro_tips: f.pro_tips.filter((_, idx) => idx !== i) }));

  const toggleTool = (tool) => {
    setForm(f => ({
      ...f,
      recommended_tools: f.recommended_tools.includes(tool)
        ? f.recommended_tools.filter(t => t !== tool)
        : [...f.recommended_tools, tool]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent_flag) { setError("Please agree to the contributor terms."); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contributor/submit`, form, { withCredentials: true });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center max-w-md fade-in">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <Check size={28} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-3">Submission Received!</h2>
          <p className="text-slate-600 mb-2">Your application has been submitted for review.</p>
          <p className="text-sm text-slate-500 mb-6">Our team will review it and notify you once it's approved.</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => { setSuccess(false); setForm({ title: "", function_id: "", category: "", business_context: "", when_to_use: "", input_required: "", main_prompt: "", expected_output: "", pro_tips: [], recommended_tools: [], consent_flag: false, terms_version: "1.0" }); }}>
              Submit Another
            </Button>
            <Button onClick={() => navigate("/profile")} className="bg-blue-600 hover:bg-blue-700 text-white">
              View My Submissions
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Contribute an Application</h1>
          <p className="text-slate-500 mt-1">Share your AI expertise with the professional community</p>
        </div>

        {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-slate-900">Basic Information</h3>
            <div>
              <Label>Application Title *</Label>
              <Input value={form.title} onChange={set("title")} required placeholder="e.g. Vendor Risk Assessment" className="mt-1" data-testid="contrib-title-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Business Function *</Label>
                <Select value={form.function_id} onValueChange={v => setForm(f => ({ ...f, function_id: v }))} required>
                  <SelectTrigger className="mt-1" data-testid="contrib-function-select">
                    <SelectValue placeholder="Select function" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUNCTIONS.map(fn => (
                      <SelectItem key={fn.id} value={fn.id}>{fn.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category *</Label>
                <Input value={form.category} onChange={set("category")} required placeholder="e.g. Risk Management" className="mt-1" data-testid="contrib-category-input" />
              </div>
            </div>
          </div>

          {/* Context */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-slate-900">Context & Usage</h3>
            <div>
              <Label>Business Context *</Label>
              <Textarea value={form.business_context} onChange={set("business_context")} required rows={3} placeholder="Describe the business problem this solves..." className="mt-1" data-testid="contrib-context-input" />
            </div>
            <div>
              <Label>When to Use *</Label>
              <Textarea value={form.when_to_use} onChange={set("when_to_use")} required rows={2} placeholder="Describe the specific situations when to use this..." className="mt-1" />
            </div>
            <div>
              <Label>Input Required *</Label>
              <Input value={form.input_required} onChange={set("input_required")} required placeholder="What information does the user need to provide?" className="mt-1" />
            </div>
          </div>

          {/* Prompt */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-slate-900">The AI Prompt</h3>
            <div>
              <Label>Main Prompt *</Label>
              <Textarea value={form.main_prompt} onChange={set("main_prompt")} required rows={10} placeholder="Write the full AI prompt here. Use [INSERT X] for user inputs..." className="mt-1 font-mono text-sm" data-testid="contrib-prompt-input" />
            </div>
            <div>
              <Label>Expected Output *</Label>
              <Textarea value={form.expected_output} onChange={set("expected_output")} required rows={3} placeholder="Describe what output the AI will produce..." className="mt-1" />
            </div>
          </div>

          {/* Tips & Tools */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-slate-900">Tips & Tools</h3>
            <div>
              <Label>Pro Tips</Label>
              <div className="flex gap-2 mt-1">
                <Input value={tipInput} onChange={e => setTipInput(e.target.value)} placeholder="Add a tip..." onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTip())} />
                <Button type="button" variant="outline" onClick={addTip}><Plus size={16} /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.pro_tips.map((tip, i) => (
                  <Badge key={i} variant="secondary" className="text-xs pr-1 flex items-center gap-1">
                    {tip}
                    <button type="button" onClick={() => removeTip(i)}><X size={10} /></button>
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>Recommended AI Tools</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TOOLS.map(tool => (
                  <button
                    key={tool}
                    type="button"
                    onClick={() => toggleTool(tool)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                      form.recommended_tools.includes(tool)
                        ? "bg-blue-50 border-blue-400 text-blue-700"
                        : "bg-white border-slate-200 text-slate-600"
                    }`}
                  >
                    {tool}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* IP Consent */}
          <div className={`border rounded-xl p-6 transition-all ${form.consent_flag ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="font-semibold text-slate-900">Intellectual Property Agreement</h3>
              {form.consent_flag && (
                <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-full font-medium">
                  <Check size={11} /> Accepted
                </span>
              )}
            </div>
            <div className="space-y-1.5 text-sm text-slate-700 mb-4 bg-white/60 rounded-lg p-4 border border-slate-100">
              <p className="font-medium text-slate-800 mb-2">By submitting, you confirm that:</p>
              <ul className="space-y-1.5 ml-2">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0 mt-1.5" />
                  This content is original or you have full rights to submit it
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0 mt-1.5" />
                  You grant Promptlee the right to use, edit, publish, and commercialize this content
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0 mt-1.5" />
                  You agree to our Contributor Terms of Service (v{form.terms_version})
                </li>
              </ul>
              <p className="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100">
                Terms version: {form.terms_version} · Your consent will be timestamped at submission
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={form.consent_flag}
                onCheckedChange={v => setForm(f => ({ ...f, consent_flag: v }))}
                data-testid="consent-checkbox"
                className={form.consent_flag ? "border-emerald-500 bg-emerald-500" : ""}
              />
              <Label htmlFor="consent" className="text-sm cursor-pointer leading-relaxed">
                I have read the above and confirm my agreement to the Contributor Terms *
              </Label>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || !form.consent_flag}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
            data-testid="contribute-submit-btn"
          >
            {loading ? <><Loader2 className="animate-spin mr-2 w-4 h-4" /> Submitting...</> : "Submit Application for Review"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ContributePage;
